import type { PortalAuthPortal } from './portalAuthIdentity';

export type PortalIdentityUserType = 'employer' | 'candidate' | 'platform_admin';

export type PortalIdentityStatus = 'active' | 'legacy_pending' | 'needs_review' | 'disabled';

export type PortalIdentityKind = 'email_password' | 'google_direct_legacy' | 'unknown';

export type PortalIdentityResolverResult = {
  ok: boolean;
  ready: boolean;
  reason: string;
  portal_auth_identity_id: string | null;
  portal: PortalAuthPortal | null;
  user_type: PortalIdentityUserType | null;
  auth_provider: string | null;
  auth_instance_id: string | null;
  auth_user_id: string | null;
  status: PortalIdentityStatus | null;
  auth_identity_kind: PortalIdentityKind | null;
};

type ResolverRpcClient = {
  rpc: (
    fn: 'resolve_current_portal_auth_identity',
    args: { _portal: PortalAuthPortal },
  ) => Promise<{ data: unknown; error: unknown }>;
};

const ALLOWED_KEYS = new Set([
  'ok',
  'ready',
  'reason',
  'portal_auth_identity_id',
  'portal',
  'user_type',
  'auth_provider',
  'auth_instance_id',
  'auth_user_id',
  'status',
  'auth_identity_kind',
]);

const SENSITIVE_KEY_PATTERN = /(email|subject|payload|token|jwt|secret|password|provider_payload|google)/i;

const EMPTY_RESULT: PortalIdentityResolverResult = {
  ok: false,
  ready: false,
  reason: 'unresolved',
  portal_auth_identity_id: null,
  portal: null,
  user_type: null,
  auth_provider: null,
  auth_instance_id: null,
  auth_user_id: null,
  status: null,
  auth_identity_kind: null,
};

function isObjectRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function getString(value: unknown) {
  return typeof value === 'string' && value.trim() ? value.trim() : null;
}

function getBoolean(value: unknown) {
  return typeof value === 'boolean' ? value : false;
}

function hasUnsafeKey(payload: Record<string, unknown>) {
  return Object.keys(payload).some((key) => !ALLOWED_KEYS.has(key) || SENSITIVE_KEY_PATTERN.test(key));
}

export function normalizePortalIdentityResolverResponse(data: unknown): PortalIdentityResolverResult {
  if (!isObjectRecord(data) || hasUnsafeKey(data)) {
    return {
      ...EMPTY_RESULT,
      reason: 'unsafe_response',
    };
  }

  return {
    ok: getBoolean(data.ok),
    ready: getBoolean(data.ready),
    reason: getString(data.reason) ?? 'unknown',
    portal_auth_identity_id: getString(data.portal_auth_identity_id),
    portal: getString(data.portal) as PortalAuthPortal | null,
    user_type: getString(data.user_type) as PortalIdentityUserType | null,
    auth_provider: getString(data.auth_provider),
    auth_instance_id: getString(data.auth_instance_id),
    auth_user_id: getString(data.auth_user_id),
    status: getString(data.status) as PortalIdentityStatus | null,
    auth_identity_kind: getString(data.auth_identity_kind) as PortalIdentityKind | null,
  };
}

export async function resolveCurrentPortalIdentity(
  client: ResolverRpcClient,
  portal: PortalAuthPortal,
): Promise<PortalIdentityResolverResult> {
  const { data, error } = await client.rpc('resolve_current_portal_auth_identity', { _portal: portal });

  if (error) {
    return {
      ...EMPTY_RESULT,
      reason: 'resolver_error',
    };
  }

  return normalizePortalIdentityResolverResponse(data);
}
